import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { cn } from '@/helpers/shadCnUtils';
import { usePermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Label } from '@/new/components/shadcn/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface SourceDocumentsOptionProps {
  includeSourceDocuments: boolean;
  orderByDate: boolean;
  onIncludeSourceDocumentsChange: (value: boolean) => void;
  onOrderByDateChange: (value: boolean) => void;
  showEmailNotification?: boolean;
}

export const SourceDocumentsOption = ({
  includeSourceDocuments,
  orderByDate,
  onIncludeSourceDocumentsChange,
  onOrderByDateChange,
  showEmailNotification = false,
}: SourceDocumentsOptionProps) => {
  const { allowed: canDownloadDocs } = usePermission(Permission.DocumentDownload);

  return (
    <div className="flex flex-col gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('flex items-center gap-3', !canDownloadDocs && 'opacity-50')}>
            <Checkbox
              id="includeSourceDocuments"
              checked={canDownloadDocs ? includeSourceDocuments : false}
              disabled={!canDownloadDocs}
              onCheckedChange={(checked) => {
                const value = Boolean(checked);
                onIncludeSourceDocumentsChange(value);
                if (!value) onOrderByDateChange(false);
              }}
            />
            <Label
              htmlFor="includeSourceDocuments"
              className={cn(
                'cursor-pointer space-y-0.5 text-sm text-gray-700',
                !canDownloadDocs && 'cursor-not-allowed',
              )}
            >
              <div className="font-medium">Include source documents</div>
              <div className="text-xs font-normal text-gray-400">
                Download a zip containing the chronology and all referenced documents
              </div>
            </Label>
          </div>
        </TooltipTrigger>
        {!canDownloadDocs && (
          <TooltipContent>
            <p>You do not have permission to download source documents</p>
          </TooltipContent>
        )}
      </Tooltip>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Checkbox
            id="orderByDate"
            disabled={!includeSourceDocuments}
            checked={orderByDate}
            onCheckedChange={(checked) => onOrderByDateChange(Boolean(checked))}
          />
          <label
            htmlFor="orderByDate"
            className={cn(
              includeSourceDocuments ? 'cursor-pointer' : 'opacity-50',
              'space-y-0.5 text-sm text-gray-700',
            )}
          >
            <div className="font-medium">Order documents by date</div>
            <div className="text-xs font-normal text-gray-400">
              Prepend each filename with its inferred document date for chronological sorting
            </div>
          </label>
        </div>
        {orderByDate && (
          <div className="mt-1 flex gap-1.75 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-600">
            <Icon className="lucide--info mt-0.5 text-amber-500" />
            Files will be renamed with a date prefix (e.g. 20250120-filename.pdf). Documents without a detected date
            will keep their original names.
          </div>
        )}
      </div>

      {showEmailNotification && includeSourceDocuments && (
        <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500">
          <Icon className="lucide--mail text-gray-500" />
          You will receive an email when the export is ready.
        </div>
      )}
    </div>
  );
};
